import { useAct } from 'FlowBuilder/store'
import InlineCkEditor from 'Shared/components/CKEditor/InlineCkEditor'
import useActionTranslate from 'FlowBuilder/hooks/useActionTranslate'
import SubscriptionTopicsSelect from 'Shared/components/SubscriptionTopicsSelect'

export default function SettingsForm({ actionId }) {
  const act = useAct()
  const [userInstructionsSourceMode, setUserInstructionsSourceMode] = useState(false)
  const hasActiveActionTemplates = useSel(s => _.values(s.actionTemplates.entities).filter(at => at.actionId === actionId && at.active).length > 0)
  const mergeTags = useSel(s => s.builder.mergeTags)
  const action = useSel(s => s.actions.entities[s.local.selectedActionId])
  const config = action.config || { decisionMakerSetId: '', emailSenderId: '' }
  const decisionMakersSet = useSel(s => s.decisionMakersSet)

  function update(data) {
    act.actions.upsert(actionId, { config: { ...config, ...data } })
  }

  const {
    language,
    textContentUpdater,
    translated: { userInstructionsContent, optInLabel },
    fallback: { userInstructionsContent: userInstructionsContentFallback, optInLabel: optInLabelFallback },
    flag
  } = useActionTranslate({ actionId })

  useEffect(() => {
    if (decisionMakersSet?.dataCollectionRequired !== null) {
      update({ requiredUserInfo: decisionMakersSet?.dataCollectionRequired })
    }
  }, [decisionMakersSet?.dataCollectionRequired])


  return <div>
    <h3 className='margin-top double'>Email content settings</h3>
    <div className='flex field'>
      <label className="bold">Allow supporters to edit subject lines</label>
      <input
        className="margin-left"
        type="checkbox"
        checked={config.editableSubject}
        onChange={e => update({ editableSubject: e.target.checked })}
      />
    </div>
    <div className='flex field margin-top'>
      <label className="bold">Allow supporters to edit template</label>
      <input
        className="margin-left"
        type="checkbox"
        checked={!config.editableBody === false}
        onChange={e => update({ editableBody: e.target.checked, requireBodyEditing: false })}
      />
    </div>
    {!hasActiveActionTemplates && config.editableBody === false && <p className='error margin-top half'>Allow supporters to edit template must be enabled if no pre-written templates are provided.</p>}
    <div className='flex field margin-top'>
      <label className="bold">Require supporters to edit template</label>
      <input
        className="margin-left"
        type="checkbox"
        checked={config.requireBodyEditing}
        onChange={e => update({ requireBodyEditing: e.target.checked, editableBody: true })}
      />
    </div>

    <h3 className='margin-top double'>What supporter information should be sent to the recipient?</h3>
    <p>Everyone sending an email will be required to complete these fields. These will be included in the emails sent.</p>
    <div className='flex field column'>
      <div>
        {[
          { value: 'name_email', label: 'Name and Email only' },
          { value: 'name_email_postcode_address', label: 'Name, Email, Postcode and Address' },
        ].map(option => (
          <div key={option.value}>
            <label className="flex margin-top" style={{ gap: '5px' }}>
              <input
                type="radio"
                name="requiredUserInfo"
                value={option.value}
                checked={config.requiredUserInfo === option.value}
                onChange={e => update({ requiredUserInfo: e.target.value })}
                disabled={decisionMakersSet?.dataCollectionRequired !== null}
              />
              {option.label}
            </label>
          </div>
        ))}
      </div>
    </div>

    <h2 className='margin-top double'>Opt-in</h2>
    <p>Optionally ask supporters to opt in to communications when they take this action.</p>
    <div className='field flex margin-top'>
      <label className="bold">Enable opt-in</label>
      <input
        className="margin-left"
        type="checkbox"
        checked={config.enableOptIn}
        onChange={e => update({ enableOptIn: e.target.checked, optInTopics: e.target.checked && !config.optInTopics?.length ? ['email:default'] : config.optInTopics })}
      />
    </div>
    {config.enableOptIn && (
      <>
        <div className='field margin-top'>
          <label className="bold">Subscriptions</label>
          <SubscriptionTopicsSelect
            value={config.optInTopics || ['email:default']}
            onChange={selectedOptions => update({ optInTopics: selectedOptions ? selectedOptions.map(o => o.value) : [] })}
            isMulti={true}
            documentPortalTarget={false}
          />
        </div>
        <div className='field margin-top'>
          <label className="bold dark-grey">Opt-in label {flag}</label>
          <input
            className="margin-top half full-width"
            type="text"
            value={optInLabel ?? ''}
            placeholder={optInLabelFallback ?? 'Keep me updated with news and campaigns'}
            onChange={e => textContentUpdater('optInLabel', e.target.value)}
          />
        </div>
      </>
    )}

    <h2 className='margin-top double'>Show introduction</h2>
    <p>If this is enabled, you can set up an image and introduction for this action. These can be modified in the Preview builder</p>
    <div className='field flex margin-top'>
      <label className="bold">Show introduction</label>
      <input
        className="margin-left"
        type="checkbox"
        checked={config.showIntroduction}
        onChange={e => update({ showIntroduction: e.target.checked })}
      />
    </div>

    <h2 className='margin-top double'>Instructions</h2>
    <p>You can optionally provide instructions or tips to supporters while they provide their details.</p>
    <div className='field flex margin-top'>
      <label className="bold">Show instructions {flag}</label>
      <input
        className="margin-left"
        type="checkbox"
        checked={config.includeUserInstructions}
        onChange={e => update({ includeUserInstructions: e.target.checked })}
      />
    </div>
    <div className='field margin-top half'>
      <InlineCkEditor
        value={userInstructionsContent ?? ''}
        placeholder={userInstructionsContentFallback}
        onUpdate={value => textContentUpdater('userInstructionsContent', value)}
        sourceMode={userInstructionsSourceMode}
        setSourceMode={setUserInstructionsSourceMode}
        mergeTags={mergeTags}
        disabled={!config.includeUserInstructions}
      />
    </div>
  </div>
}
